/**
 * <highlighted-product-advanced>
 *
 * Client-side-only PDP-style variant switching + add-to-cart for a product
 * that is picked via a section setting and rendered on an arbitrary page
 * template (not the product's own /products/{handle} URL).
 *
 * Modeled on the `product-card` custom element (assets/global.js) — variant
 * resolution and price/media/availability updates are computed entirely
 * from an embedded JSON blob, no fetch to Shopify's Section Rendering API.
 * Add-to-cart follows the exact fetch('/cart/add.js', ...) pattern used by
 * ProductStickyCart in sections/main-product.liquid, including bundling any
 * selected Popular Add-ons.
 *
 * This element never touches window.history or window.location: it is
 * never the canonical URL for the product it renders.
 */
if (!customElements.get('highlighted-product-advanced')) {
  customElements.define(
    'highlighted-product-advanced',
    class HighlightedProductAdvanced extends HTMLElement {
      constructor() {
        super();
        this.variants = this.parseVariants();
        this.currentVariant = this.getInitialVariant();
      }

      connectedCallback() {
        this.optionPicker = this.querySelector('[data-highlighted-product-advanced-variant-picker]');
        if (this.optionPicker) {
          this.optionPicker.addEventListener('change', this.onOptionChange.bind(this));
        }

        this.addToCartButton = this.querySelector('[data-highlighted-product-advanced-add-to-cart]');
        if (this.addToCartButton) {
          this.addToCartButton.addEventListener('click', this.onAddToCart.bind(this));
        }

        // Sync the initial UI in case the section's default selected variant
        // (Liquid-rendered) doesn't match the first entry read back from JSON.
        if (this.currentVariant) {
          this.updateVariantUI(this.currentVariant, { silent: true });
        }

        this.initStickyAtc();
      }

      // Sticky add-to-cart bar (sections/highlighted-product-advanced.liquid,
      // `enable_sticky_atc`): visible whenever neither the page's first
      // section (the "hero") nor this section's own buy box is in view,
      // "Shop now" just scrolls back up to the real buy box.
      initStickyAtc() {
        const bar = this.querySelector('[data-sticky-atc-bar]');
        if (!bar) return;
        if (window.Shopify && Shopify.designMode) return;

        const heroTarget = document.getElementById('MainContent')?.firstElementChild;
        const sectionTarget = document.getElementById(`shopify-section-${this.dataset.section}`);

        let heroVisible = false;
        let sectionVisible = false;
        const updateVisibility = () => {
          bar.classList.toggle('is-hidden', heroVisible || sectionVisible);
        };

        if (heroTarget) {
          new IntersectionObserver((entries) => {
            heroVisible = entries[entries.length - 1].isIntersecting;
            updateVisibility();
          }).observe(heroTarget);
        }

        if (sectionTarget) {
          new IntersectionObserver((entries) => {
            sectionVisible = entries[entries.length - 1].isIntersecting;
            updateVisibility();
          }).observe(sectionTarget);
        }

        const scrollButton = bar.querySelector('[data-sticky-atc-scroll]');
        const scrollTarget = this.querySelector('.product-form__buttons');
        if (scrollButton && scrollTarget) {
          scrollButton.addEventListener('click', () => {
            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      }

      parseVariants() {
        const script = this.querySelector('[data-highlighted-product-advanced-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent) || [];
        } catch (error) {
          console.error('HighlightedProductAdvanced: could not parse variants JSON', error);
          return [];
        }
      }

      getInitialVariant() {
        const idInput = this.querySelector('[data-highlighted-product-advanced-variant-id-input]');
        const id = idInput ? parseInt(idInput.value, 10) : null;
        if (id) {
          const match = this.variants.find((variant) => variant.id === id);
          if (match) return match;
        }
        return this.variants[0] || null;
      }

      getSelectedOptionValues() {
        const values = [];
        this.querySelectorAll('[data-option-position]:checked').forEach((input) => {
          const position = parseInt(input.dataset.optionPosition, 10);
          if (position) values[position - 1] = input.value;
        });
        return values;
      }

      findVariantForOptions(selectedValues) {
        if (!selectedValues.length) return this.currentVariant;
        return this.variants.find((variant) =>
          selectedValues.every((value, index) => {
            if (value == null) return true;
            return variant[`option${index + 1}`] === value;
          })
        );
      }

      onOptionChange(event) {
        const target = event.target;
        if (!target || !target.hasAttribute('data-option-position')) return;

        const fieldset = target.closest('fieldset');
        const selectedValueLabel = fieldset && fieldset.querySelector('[data-highlighted-product-advanced-selected-value]');
        if (selectedValueLabel) selectedValueLabel.textContent = target.value;

        const selectedValues = this.getSelectedOptionValues();
        const variant = this.findVariantForOptions(selectedValues);
        this.updateVariantUI(variant);
      }

      updateVariantUI(variant, { silent = false } = {}) {
        this.currentVariant = variant || null;

        this.updatePrice(variant);
        this.updateSku(variant);
        this.updateAddToCartButton(variant);
        this.updateHiddenVariantInputs(variant);
        this.updateMedia(variant);
        this.updatePickupAvailability(variant);
        this.updateInventoryNotice(variant);

        if (!silent) this.dispatchVariantChange(variant);
      }

      getMoneyFormat() {
        const priceContainer = this.querySelector('.price__container');
        return (
          (priceContainer && priceContainer.dataset.moneyFormat) ||
          (window.theme && window.theme.settings && window.theme.settings.money_format) ||
          '${{amount}}'
        );
      }

      updatePrice(variant) {
        if (!variant) return;
        const priceContainers = this.querySelectorAll('.price__container');
        if (!priceContainers.length) return;

        const moneyFormat = this.getMoneyFormat();
        const price = typeof formatPrice === 'function' ? formatPrice(moneyFormat, variant.price / 100) : variant.price;
        const compareAtPrice =
          typeof formatPrice === 'function' && variant.compare_at_price
            ? formatPrice(moneyFormat, variant.compare_at_price / 100)
            : null;

        // Two containers exist when the sticky add-to-cart bar is enabled
        // (sections/highlighted-product-advanced.liquid renders its own
        // `render 'price'` for the bar) — keep both in sync on every change.
        priceContainers.forEach((priceContainer) => {
          const labelPriceRegular = priceContainer.dataset.labelPriceRegular || '';
          const labelPriceSale = priceContainer.dataset.labelPriceSale || '';
          const labelPriceSave = priceContainer.dataset.labelPriceSave || '';
          const showSavingsBadge = priceContainer.dataset.forceSavingsBadge === 'true';

          if (compareAtPrice && parseFloat(variant.compare_at_price) > parseFloat(variant.price)) {
            const savingsPercentage = Math.floor(
              ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
            );
            const savingsBadge = showSavingsBadge
              ? `<span class="savings_badge color-scheme-358911b0-a463-4cbc-863a-ca414e1bf634">${labelPriceSave} ${savingsPercentage}%</span>`
              : '';
            priceContainer.innerHTML = `
              <div class="price__sale${showSavingsBadge ? ' price__sale--has-badge' : ''}">
                <div class="price__sale-inner">
                  <span class="visually-hidden">${labelPriceRegular}</span>
                  <s>${compareAtPrice}</s>
                  <ins>
                    <span class="visually-hidden">${labelPriceSale}</span>
                    ${price}
                  </ins>
                </div>
                ${savingsBadge}
              </div>
            `;
          } else {
            priceContainer.innerHTML = `
              <div class="price__regular">
                <span class="visually-hidden">${labelPriceRegular}</span>
                <span>${price}</span>
              </div>
            `;
          }
        });

        const priceWrappers = this.querySelectorAll('.price');
        priceWrappers.forEach((priceWrapper) => priceWrapper.classList.toggle('price--sold-out', !variant.available));
      }

      updateSku(variant) {
        const skuWrapper = this.querySelector('[data-highlighted-product-advanced-sku]');
        if (!skuWrapper) return;
        const skuText = skuWrapper.querySelector('[data-highlighted-product-advanced-sku-text]');
        if (variant && variant.sku) {
          if (skuText) skuText.textContent = `#${variant.sku}`;
          skuWrapper.classList.remove('hidden');
        } else {
          skuWrapper.classList.add('hidden');
        }
      }

      updateAddToCartButton(variant) {
        const button = this.addToCartButton || this.querySelector('[data-highlighted-product-advanced-add-to-cart]');
        if (!button) return;
        const textEl = button.querySelector('[data-add-to-cart-button-text]');
        const priceEl = button.querySelector('[data-add-to-cart-button-price]');

        if (!variant) {
          button.setAttribute('disabled', 'disabled');
          if (textEl) textEl.textContent = button.dataset.unavailableText || textEl.textContent;
          if (priceEl) priceEl.textContent = '';
          return;
        }

        button.toggleAttribute('disabled', !variant.available);

        if (textEl) {
          textEl.textContent = variant.available
            ? button.dataset.addToCartText || textEl.textContent
            : button.dataset.soldOutText || textEl.textContent;
        }

        if (priceEl) {
          if (variant.available) {
            const moneyFormat = this.getMoneyFormat();
            const price = typeof formatPrice === 'function' ? formatPrice(moneyFormat, variant.price / 100) : variant.price;
            const showComparePrice = button.dataset.showComparePrice === 'true';
            const compareAtPrice =
              showComparePrice &&
              typeof formatPrice === 'function' &&
              parseFloat(variant.compare_at_price) > parseFloat(variant.price)
                ? formatPrice(moneyFormat, variant.compare_at_price / 100)
                : null;
            priceEl.innerHTML = compareAtPrice ? ` - <s>${compareAtPrice}</s> ${price}` : ` - ${price}`;
          } else {
            priceEl.textContent = '';
          }
        }
      }

      updateHiddenVariantInputs(variant) {
        this.querySelectorAll('[data-highlighted-product-advanced-variant-id-input]').forEach((input) => {
          input.value = variant ? variant.id : '';
        });
      }

      updateMedia(variant) {
        if (!variant || !variant.featured_media) return;
        const gallery = this.querySelector('swiper-product-gallery');
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(variant.featured_media.id);
        }
      }

      updatePickupAvailability(variant) {
        const pickup = this.querySelector('pickup-availability');
        if (!pickup || !variant) return;
        pickup.dataset.variantId = variant.id;
        if (typeof pickup.fetchAvailability === 'function') pickup.fetchAvailability(variant.id);
      }

      updateInventoryNotice(variant) {
        const notice = this.querySelector('[data-highlighted-product-advanced-inventory-notice]');
        if (!notice) return;
        const threshold = parseInt(notice.dataset.inventoryThreshold, 10) || 0;
        const showNotice =
          variant &&
          variant.inventory_management === 'shopify' &&
          variant.available &&
          variant.inventory_quantity <= threshold;
        notice.classList.toggle('hidden', !showNotice);
        const countEl = notice.querySelector('[data-highlighted-product-advanced-inventory-count]');
        if (countEl && variant) countEl.textContent = variant.inventory_quantity;
      }

      dispatchVariantChange(variant) {
        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant, sectionId: this.dataset.section },
          })
        );

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.dataset.section, variant },
          });
        }
      }

      getCartAddUrl() {
        if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
          return `${window.Shopify.routes.root}cart/add.js`;
        }
        if (window.routes && window.routes.cart_add_url) {
          return `${window.routes.cart_add_url.replace(/\/(json)?$/, '')}.js`;
        }
        return '/cart/add.js';
      }

      showError(message) {
        const errorEl = this.querySelector('[data-highlighted-product-advanced-error-message]');
        if (!errorEl) return;
        errorEl.textContent = message || '';
        errorEl.classList.toggle('hidden', !message);
      }

      async onAddToCart(event) {
        event.preventDefault();
        const button = event.currentTarget;
        if (!button || button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

        const variant = this.currentVariant;
        if (!variant || !variant.available) return;

        const quantityInput = this.querySelector('.quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

        const items = [{ id: variant.id, quantity }];

        const selectedAddons =
          window.popularAddons && typeof window.popularAddons.getSelectedAddons === 'function'
            ? window.popularAddons.getSelectedAddons()
            : [];
        selectedAddons.forEach((addon) => {
          items.push({ id: parseInt(addon.variantId, 10), quantity: 1 });
        });

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        this.showError('');

        try {
          const response = await fetch(this.getCartAddUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json();

          if (response.ok) {
            if (typeof updateCartCounters === 'function') updateCartCounters();
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'highlighted-product-advanced',
                productVariantId: variant.id,
                cartData: data,
              });
            }
            if (typeof window.SLIDECART_UPDATE === 'function') {
              window.SLIDECART_UPDATE(() => {
                if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
              });
            } else if (typeof window.SLIDECART_OPEN === 'function') {
              window.SLIDECART_OPEN();
            }
          } else {
            this.showError(data.description || data.message || 'Error adding to cart');
          }
        } catch (error) {
          console.error('HighlightedProductAdvanced: add to cart failed', error);
          this.showError('Error adding to cart');
        } finally {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }
      }
    }
  );
}
